import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { getParamValue, usePagePrefs } from "@pepsico/pref-permissions";
import Map, { type MapMouseEvent, type MapRef } from "react-map-gl/mapbox";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import type {
  GrowerGeoResponse,
  PlotGeoResponse,
  RowGeoResponse,
  RowSnipResponse,
  SiteGeoResponse,
  TrialGeoResponse,
} from "api/model";
import { useGetPlotsGeoByLocation } from "api/plots-geo/plots-geo";
import { useGetRowsGeo } from "api/rows-geo/rows-geo";
import { useGetRowSnips } from "api/snips/snips";
import { useGetSitesGeoByLocation } from "api/sites-geo/sites-geo";
import { useGetTrialsGeoByLocation } from "api/trials-geo/trials-geo";
import { useCogOrtho } from "context/CogOrtho";
import { usePlotMap } from "context/PlotMap";
import {
  fromBoundaryMultiPolygon,
  hasBoundaryGeoJson,
  MIN_BOUNDARY_POINTS_TO_CLOSE,
  toBoundariesGeoJson,
  toBoundaryGeoJson,
} from "context/PlotMap/boundaryUtils";
import { type Bounds, type LngLatRing } from "context/PlotMap/geo";
import {
  toRowDividerGeoJson,
  toRowLabelGeoJson,
} from "context/PlotMap/gridRowSplit";
import { toGridGeoJson } from "context/PlotMap/gridUtils";
import {
  getTrialGeoLabel,
  plotGeosToPlotGrid,
  toPlotGridTrialLabelsGeoJson,
} from "context/PlotMap/plotGridGeoUtils";
import {
  COLOR_TARGET_PREF_KEY,
  PLOT_COLOR_PREF_KEY,
  type PlotColorBy,
} from "defs";

import CogOrthoMapLayers from "./CogOrthoMapLayers";
import LocationMarkers from "./LocationMarkers";
import PlotMapLayers from "./PlotMapLayers";
import useBoundaryMapInteraction from "./hooks/useBoundaryMapInteraction";
import useGridTransformInteraction from "./hooks/useGridTransformInteraction";
import useMeshEditInteraction from "./hooks/useMeshEditInteraction";
import {
  readSelectedGrowerGeoId,
  readSelectedSiteGeoId,
  readSelectedTrialGeoId,
} from "./utils/selectionPrefs";
import { addLabelPlaqueImages } from "./utils/labelPlaque";
import {
  GROWER_BOUNDARY_DRAFT_LINE_LAYER_ID,
  SITE_BOUNDARY_DRAFT_LINE_LAYER_ID,
  isGrowerLabelLayerVisible,
  isPlotLabelLayerVisible,
  isPlotMapLayerVisible,
  isRowLabelLayerVisible,
  isSiteLabelLayerVisible,
  toMapboxVisibility,
} from "./utils/layerPrefs";
import {
  fitMapToLocationView,
  getBoundariesBounds,
} from "./utils/mapCameraUtils";
import {
  EMPTY_FEATURE_COLLECTION,
  US_INITIAL_VIEW,
} from "./utils/mapConstants";
import {
  applyPlotAnalysisFillColors,
  getPlotAnalysisFillColors,
  getRowAnalysisFillColors,
  toRowAnalysisFillGeoJson,
} from "./utils/plotAnalysisFill";

const defaultRowSnips: RowSnipResponse[] = [];

const stampDimmed = <T extends { properties?: object | null }>(
  features: readonly T[],
  dimmed: boolean,
): T[] =>
  features.map((feature) => ({
    ...feature,
    properties: { ...feature.properties, dimmed },
  }));

const parsePlotColorPref = (
  value: string | string[] | null | undefined,
): PlotColorBy => {
  if (value === "emerg" || value === "rc") {
    return value;
  }
  return "none";
};

const parseCoordPref = (
  value: string | string[] | undefined,
): number | null => {
  if (typeof value !== "string" || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

const tryApplyLocationView = ({
  boundaryBounds,
  fittedLocationUuidRef,
  hasLatLng,
  isLocationViewReady,
  isReady,
  lat,
  lng,
  locationUuid,
  mapRef,
}: {
  boundaryBounds: Bounds | undefined;
  fittedLocationUuidRef: RefObject<string | null>;
  hasLatLng: boolean;
  isLocationViewReady: boolean;
  isReady: boolean;
  lat: number | null;
  lng: number | null;
  locationUuid: string;
  mapRef: RefObject<MapRef | null>;
}): void => {
  if (!isReady || !isLocationViewReady || mapRef.current == null) {
    return;
  }

  if (locationUuid === "") {
    fittedLocationUuidRef.current = "";
    return;
  }

  if (fittedLocationUuidRef.current === locationUuid) {
    return;
  }

  const didFit = fitMapToLocationView({
    boundaryBounds,
    hasLatLng,
    lat,
    lng,
    map: mapRef.current,
  });

  if (didFit) {
    fittedLocationUuidRef.current = locationUuid;
  }
};

/**
 * Ctrl/Cmd state from a keyboard event. Keyup of Control or Meta ignores that
 * key's own flag, which can still be true in the event, and keeps the other.
 */
const rotateModifierFromKeyboardEvent = (event: KeyboardEvent): boolean => {
  if (event.key === "Control") {
    return event.type === "keydown" || event.metaKey;
  }
  if (event.key === "Meta") {
    return event.type === "keydown" || event.ctrlKey;
  }
  return event.ctrlKey || event.metaKey;
};

interface PlotMapViewProps {}

const PlotMapView: React.FC<PlotMapViewProps> = () => {
  const mapRef = useRef<MapRef | null>(null);
  const fittedLocationUuidRef = useRef<string | null>(null);
  const lastMouseEventRef = useRef<MapMouseEvent | null>(null);
  const rotateModifierRef = useRef(false);
  const shiftKeyRef = useRef(false);
  const [rotateModifierHeld, setRotateModifierHeld] = useState(false);
  const {
    boundary: {
      addPoint: addBoundaryPoint,
      close: closeBoundary,
      editingGrowerGeoId,
      editingSiteGeoId,
      endHistoryGesture: endBoundaryHistoryGesture,
      insertPoint: insertBoundaryPoint,
      isClosed: isBoundaryClosed,
      isGrowerBoundaryMode,
      isSiteBoundaryMode,
      label: boundaryLabel,
      points: boundaryPoints,
      removePoint: removeBoundaryPoint,
      updatePoint: updateBoundaryPoint,
    },
    plot: {
      beginHistoryGesture: beginGridHistoryGesture,
      clearSelection: clearGridSelection,
      current: grid,
      deleteCell: deleteGridCell,
      deleteEdge: deleteGridEdge,
      deleteVertex: deleteGridVertex,
      detachAndTranslateSelection,
      endHistoryGesture: endGridHistoryGesture,
      finalizeEdgeSlide: finalizeGridEdgeSlide,
      growerGeoId: selectedGrowerGeoId,
      insertVertexOnEdge: insertGridVertexOnEdge,
      isDirty: isGridDirty,
      isPlotGridMode,
      loadSaved: loadSavedGrid,
      moveEdge: moveGridEdge,
      moveVertex: moveGridVertex,
      rotateContiguousCells,
      selectedCellIds,
      selectedVertexId,
      setSelectedCellIds,
      setSelectedVertexId,
      siteGeoId: selectedSiteGeoId,
      slideEdge: slideGridEdge,
      slideVertex: slideGridVertex,
      toggleCellSelection,
      trialId: selectedTrialId,
      updateTransform: updateGridTransform,
    },
  } = usePlotMap();
  const { orthos, sessions } = useCogOrtho();
  const analysisSelectedOrthoId =
    orthos.find((ortho) => ortho.isVisible)?.id ?? null;
  const [hoverCursor, setHoverCursor] = useState<string | undefined>(undefined);

  const [prefs, isReady] = usePagePrefs();
  const boundaryVisibility = toMapboxVisibility(
    isPlotMapLayerVisible(prefs, "bnd"),
  );
  const growerLabelVisibility = toMapboxVisibility(
    isGrowerLabelLayerVisible(prefs),
  );
  const growerDraftVisibility = isGrowerBoundaryMode
    ? boundaryVisibility
    : "none";
  const growerDraftLabelVisibility = isGrowerBoundaryMode
    ? growerLabelVisibility
    : "none";
  const plotGridVisibility = toMapboxVisibility(
    isPlotMapLayerVisible(prefs, "grid"),
  );
  const plotLabelVisibility = toMapboxVisibility(
    isPlotLabelLayerVisible(prefs),
  );
  const rowBoundaryVisibility = toMapboxVisibility(
    isPlotMapLayerVisible(prefs, "row"),
  );
  const rowLabelVisibility = toMapboxVisibility(isRowLabelLayerVisible(prefs));
  const siteBoundaryVisibility = toMapboxVisibility(
    isPlotMapLayerVisible(prefs, "site"),
  );
  const siteLabelVisibility = toMapboxVisibility(
    isSiteLabelLayerVisible(prefs),
  );
  const siteDraftVisibility = isSiteBoundaryMode
    ? siteBoundaryVisibility
    : "none";
  const siteDraftLabelVisibility = isSiteBoundaryMode
    ? siteLabelVisibility
    : "none";
  const lat = parseCoordPref(prefs.lat);
  const lng = parseCoordPref(prefs.lng);
  const hasLatLng = lat != null && lng != null;
  const locationUuid = (prefs.location as string | null | undefined) || "";
  const plotColor = parsePlotColorPref(
    getParamValue(prefs, PLOT_COLOR_PREF_KEY),
  );
  const { data: trialsGeo = [] } = useGetTrialsGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): TrialGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const analysisTrialId = readSelectedTrialGeoId(
    prefs,
    trialsGeo.map((trial) => trial.id),
  );
  const colorTarget = getParamValue(prefs, COLOR_TARGET_PREF_KEY);
  const hasAnalysisSelection =
    plotColor !== "none" &&
    analysisSelectedOrthoId != null &&
    analysisTrialId != null;
  const shouldColorPlots = hasAnalysisSelection && colorTarget === "plots";
  const shouldColorRows = hasAnalysisSelection && !shouldColorPlots;
  const { data: analysisRowSnips = defaultRowSnips } = useGetRowSnips(
    {
      ortho_id: analysisSelectedOrthoId ?? undefined,
      trial_id: analysisTrialId ?? undefined,
    },
    {
      query: {
        enabled: hasAnalysisSelection,
        select: (res): RowSnipResponse[] =>
          res.status === 200 ? res.data : defaultRowSnips,
      },
    },
  );
  // Plot values aggregate the row snips, so both targets share one query.
  // Kept after probe: React Compiler did not stabilize this Map across re-renders.
  const plotFillColorById = useMemo(
    () =>
      shouldColorPlots
        ? getPlotAnalysisFillColors(plotColor, analysisRowSnips)
        : new globalThis.Map<number, string>(),
    [analysisRowSnips, plotColor, shouldColorPlots],
  );
  const rowFillColorById = useMemo(
    () =>
      shouldColorRows
        ? getRowAnalysisFillColors(plotColor, analysisRowSnips)
        : new globalThis.Map<number, string>(),
    [analysisRowSnips, plotColor, shouldColorRows],
  );
  const { data: growersGeo = [], isPending: isGrowersGeoPending } =
    useGetGrowersGeoByLocation(
      { location_uuid: locationUuid },
      {
        query: {
          enabled: locationUuid !== "",
          select: (res): GrowerGeoResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );
  const { data: plotsGeo = [], isPending: isPlotsGeoPending } =
    useGetPlotsGeoByLocation(
      { location_uuid: locationUuid },
      {
        query: {
          enabled: locationUuid !== "",
          select: (res): PlotGeoResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );
  const { data: rowsGeo = [], isPending: isRowsGeoPending } = useGetRowsGeo(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): RowGeoResponse[] => (res.status === 200 ? res.data : []),
      },
    },
  );
  const { data: sitesGeo = [] } = useGetSitesGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): SiteGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const trialsById = new globalThis.Map<number, TrialGeoResponse>();
  trialsGeo.forEach((trial) => {
    trialsById.set(trial.id, trial);
  });
  const isLocationViewReady = locationUuid === "" || !isGrowersGeoPending;
  const displayedOrthoId =
    orthos.find((ortho) => sessions[ortho.id] != null)?.id ?? null;

  // The editable grid covers the selected trial; every other trial is drawn by
  // the read-only saved-plots layer below.
  const selectedTrialPlots =
    selectedTrialId == null
      ? []
      : plotsGeo.filter((plot) => plot.trial_id === selectedTrialId);
  const otherTrialPlots = plotsGeo.filter(
    (plot) => plot.trial_id !== selectedTrialId,
  );

  // Server → grid state. Always loadSaved (including empty); never cancelDraft.
  // Saved row polygons are part of the editable mesh so dividers can be dragged.
  useEffect(() => {
    if (
      isPlotGridMode ||
      isGridDirty ||
      isPlotsGeoPending ||
      (locationUuid !== "" && isRowsGeoPending)
    ) {
      return;
    }

    const selectedTrialPlotIds = new Set(
      selectedTrialPlots.map((plot) => plot.id),
    );
    loadSavedGrid(
      plotGeosToPlotGrid(
        selectedTrialPlots,
        rowsGeo.filter((row) => selectedTrialPlotIds.has(row.plot_id)),
      ),
    );
  }, [
    isGridDirty,
    isPlotGridMode,
    isPlotsGeoPending,
    isRowsGeoPending,
    loadSavedGrid,
    locationUuid,
    rowsGeo,
    // React Compiler stabilizes this array; oxlint cannot see that.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    selectedTrialPlots,
  ]);

  const isBoundaryDrawMode = isGrowerBoundaryMode || isSiteBoundaryMode;
  const canDrawBoundaryPoints = isBoundaryDrawMode && !isBoundaryClosed;
  const canInsertOnEdge = isBoundaryDrawMode && isBoundaryClosed;
  const canManipulateVertices = isBoundaryDrawMode && boundaryPoints.length > 0;
  const canCloseBoundary =
    canDrawBoundaryPoints &&
    boundaryPoints.length >= MIN_BOUNDARY_POINTS_TO_CLOSE;
  const editingSite = sitesGeo.find((site) => site.id === editingSiteGeoId);
  const linkedGrower =
    editingSite == null
      ? null
      : (growersGeo.find((grower) => grower.id === editingSite.grower_id) ??
        null);
  const growerConstraintPoints =
    isSiteBoundaryMode &&
    linkedGrower?.boundary != null &&
    hasBoundaryGeoJson(linkedGrower.boundary)
      ? fromBoundaryMultiPolygon(linkedGrower.boundary)
      : null;
  const growerConstraintRing: LngLatRing | null =
    growerConstraintPoints == null
      ? null
      : growerConstraintPoints.map((point) => [
          point.longitude,
          point.latitude,
        ]);
  const growerBoundaryBounds = getBoundariesBounds(
    growersGeo.map((grower) => grower.boundary),
  );
  const canManipulateGridTransform =
    isPlotGridMode && grid != null && !grid.isCommitted;
  const canEditMesh =
    isPlotGridMode && grid != null && grid.isCommitted && grid.mesh != null;

  const isShiftHeld = (event: MapMouseEvent): boolean => {
    const native = event.originalEvent;
    const fromEvent =
      native.shiftKey ||
      (typeof native.getModifierState === "function" &&
        native.getModifierState("Shift"));
    return Boolean(fromEvent || shiftKeyRef.current);
  };

  const isRotateModifierHeld = (event: MapMouseEvent): boolean => {
    const native = event.originalEvent;
    const fromEvent =
      native.ctrlKey ||
      native.metaKey ||
      (typeof native.getModifierState === "function" &&
        (native.getModifierState("Control") ||
          native.getModifierState("Meta")));
    return Boolean(fromEvent || rotateModifierRef.current);
  };

  const meshInteraction = useMeshEditInteraction({
    beginGridHistoryGesture,
    canEditMesh,
    clearGridSelection,
    deleteGridCell,
    deleteGridEdge,
    deleteGridVertex,
    detachAndTranslateSelection,
    endGridHistoryGesture,
    finalizeGridEdgeSlide,
    insertGridVertexOnEdge,
    isRotateModifierHeld,
    isShiftHeld,
    mapRef,
    mesh: grid?.mesh,
    moveGridEdge,
    moveGridVertex,
    rotateContiguousCells,
    selectedCellIds,
    setSelectedCellIds,
    setSelectedVertexId,
    slideGridEdge,
    slideGridVertex,
    toggleCellSelection,
  });

  const gridTransformInteraction = useGridTransformInteraction({
    beginGridHistoryGesture,
    canManipulateGridTransform,
    endGridHistoryGesture,
    grid,
    isRotateModifierHeld,
    mapRef,
    updateGridTransform,
  });

  const boundaryInteraction = useBoundaryMapInteraction({
    addBoundaryPoint,
    boundaryLineLayerId: isSiteBoundaryMode
      ? SITE_BOUNDARY_DRAFT_LINE_LAYER_ID
      : GROWER_BOUNDARY_DRAFT_LINE_LAYER_ID,
    boundaryPoints,
    canCloseBoundary,
    canDrawBoundaryPoints,
    canInsertOnEdge,
    canManipulateVertices,
    closeBoundary,
    constrainToRing: growerConstraintRing,
    endBoundaryHistoryGesture,
    insertBoundaryPoint,
    mapRef,
    removeBoundaryPoint,
    updateBoundaryPoint,
  });

  const isDragging =
    meshInteraction.isDragging ||
    gridTransformInteraction.isDragging ||
    boundaryInteraction.isDragging;

  useEffect(() => {
    tryApplyLocationView({
      boundaryBounds: growerBoundaryBounds,
      fittedLocationUuidRef,
      hasLatLng,
      isLocationViewReady,
      isReady,
      lat,
      lng,
      locationUuid,
      mapRef,
    });
  }, [
    growerBoundaryBounds,
    hasLatLng,
    isLocationViewReady,
    isReady,
    lat,
    lng,
    locationUuid,
  ]);

  useEffect(() => {
    const syncModifierKeys = (event: KeyboardEvent) => {
      shiftKeyRef.current = event.shiftKey || event.key === "Shift";
      if (event.type === "keyup" && event.key === "Shift") {
        shiftKeyRef.current = false;
      }

      const rotateHeld = rotateModifierFromKeyboardEvent(event);
      if (rotateHeld === rotateModifierRef.current) {
        return;
      }
      rotateModifierRef.current = rotateHeld;
      setRotateModifierHeld(rotateHeld);
    };
    const handleBlur = () => {
      shiftKeyRef.current = false;
      if (!rotateModifierRef.current) {
        return;
      }
      rotateModifierRef.current = false;
      setRotateModifierHeld(false);
    };
    window.addEventListener("keydown", syncModifierKeys);
    window.addEventListener("keyup", syncModifierKeys);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", syncModifierKeys);
      window.removeEventListener("keyup", syncModifierKeys);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  const handleMapLoad = () => {
    const map = mapRef.current?.getMap();
    if (map != null) {
      addLabelPlaqueImages(map);
    }
    tryApplyLocationView({
      boundaryBounds: growerBoundaryBounds,
      fittedLocationUuidRef,
      hasLatLng,
      isLocationViewReady,
      isReady,
      lat,
      lng,
      locationUuid,
      mapRef,
    });
  };

  const handleMapClick = (event: MapMouseEvent) => {
    if (isDragging) {
      return;
    }

    if (meshInteraction.handleMapClick(event)) {
      return;
    }

    boundaryInteraction.handleMapClick(event);
  };

  const handleMouseDown = (event: MapMouseEvent) => {
    if (gridTransformInteraction.handleMouseDown(event)) {
      return;
    }
    if (meshInteraction.handleMouseDown(event)) {
      return;
    }
    boundaryInteraction.handleMouseDown(event);
  };

  const resolveIdleHoverCursor = (
    event: MapMouseEvent,
    rotateHeld?: boolean,
  ): string | undefined =>
    gridTransformInteraction.resolveHoverCursor(event, rotateHeld) ??
    meshInteraction.resolveHoverCursor(event, rotateHeld) ??
    (canDrawBoundaryPoints ? "crosshair" : undefined);
  const resolveIdleHoverCursorRef = useRef(resolveIdleHoverCursor);
  resolveIdleHoverCursorRef.current = resolveIdleHoverCursor;

  useEffect(() => {
    const event = lastMouseEventRef.current;
    if (event == null || isDragging) {
      return;
    }
    setHoverCursor(
      resolveIdleHoverCursorRef.current(event, rotateModifierHeld),
    );
  }, [isDragging, rotateModifierHeld]);

  const handleMouseMove = (event: MapMouseEvent) => {
    lastMouseEventRef.current = event;
    if (!isDragging) {
      setHoverCursor(resolveIdleHoverCursor(event));
    }

    if (meshInteraction.handleMouseMove(event)) {
      return;
    }
    if (gridTransformInteraction.handleMouseMove(event)) {
      return;
    }
    boundaryInteraction.handleMouseMove(event);
  };

  const handleMouseUp = () => {
    meshInteraction.handleMouseUp();
    gridTransformInteraction.handleMouseUp();
    boundaryInteraction.handleMouseUp();
  };

  const handleContextMenu = (event: MapMouseEvent) => {
    if (
      (canManipulateGridTransform || canEditMesh) &&
      isRotateModifierHeld(event)
    ) {
      event.preventDefault();
      event.originalEvent.preventDefault();
      return;
    }
    if (meshInteraction.handleContextMenu(event)) {
      return;
    }
    boundaryInteraction.handleContextMenu(event);
  };

  const selectedGrowerPrefId = readSelectedGrowerGeoId(
    prefs,
    growersGeo.map((grower) => grower.id),
  );
  const selectedSitePrefId = readSelectedSiteGeoId(
    prefs,
    sitesGeo.map((site) => site.id),
  );
  const isAddingGrower = isGrowerBoundaryMode && editingGrowerGeoId == null;
  // Fade growers outside the selection, every saved grower while adding a new
  // one, or growers outside the plot grid being edited. Sites, trials, plots,
  // and rows of that grower fade with it. A selected site fades every other
  // site and the trials, plots, and rows that belong to those sites.
  const isGrowerDimmed = (growerId: number | null | undefined): boolean =>
    growerId != null &&
    (isAddingGrower ||
      (selectedGrowerPrefId != null && growerId !== selectedGrowerPrefId) ||
      (isPlotGridMode && growerId !== selectedGrowerGeoId));
  const isSitePrefDimmed = (siteId: number | null | undefined): boolean =>
    siteId != null &&
    selectedSitePrefId != null &&
    siteId !== selectedSitePrefId;
  const growerBoundariesGeoJson = toBoundariesGeoJson(
    growersGeo
      .filter((grower) => grower.id !== editingGrowerGeoId)
      .map((grower) => ({
        boundary: grower.boundary,
        isDimmed: isGrowerDimmed(grower.id),
        label: grower.grower_label,
      })),
  );
  const growerDraftGeoJson = isGrowerBoundaryMode
    ? toBoundaryGeoJson(boundaryPoints, isBoundaryClosed, boundaryLabel)
    : EMPTY_FEATURE_COLLECTION;
  const siteBoundariesGeoJson = toBoundariesGeoJson(
    sitesGeo
      .filter((site) => site.id !== editingSiteGeoId)
      .map((site) => ({
        boundary: site.boundary,
        isDimmed:
          isGrowerDimmed(site.grower_id) ||
          isSitePrefDimmed(site.id) ||
          (isPlotGridMode && site.id !== selectedSiteGeoId),
        label: site.site_label,
      })),
  );
  const siteDraftGeoJson = isSiteBoundaryMode
    ? toBoundaryGeoJson(boundaryPoints, isBoundaryClosed, boundaryLabel)
    : EMPTY_FEATURE_COLLECTION;
  // While editing, dim every other trial; when idle, draw all location plots here.
  const savedPlotsSource = isPlotGridMode ? otherTrialPlots : plotsGeo;
  const savedPlotIds = new Set(savedPlotsSource.map((plot) => plot.id));
  const growerIdBySiteId = new globalThis.Map(
    sitesGeo.map((site) => [site.id, site.grower_id ?? null] as const),
  );
  const isSavedPlotDimmed = (plot: PlotGeoResponse): boolean => {
    const siteId = trialsById.get(plot.trial_id)?.site_id;
    if (siteId == null) {
      return false;
    }
    return (
      isGrowerDimmed(growerIdBySiteId.get(siteId)) || isSitePrefDimmed(siteId)
    );
  };
  const dimmedSavedPlots = savedPlotsSource.filter(isSavedPlotDimmed);
  const brightSavedPlots = savedPlotsSource.filter(
    (plot) => !isSavedPlotDimmed(plot),
  );
  const dimmedSavedPlotIds = new Set(dimmedSavedPlots.map((plot) => plot.id));
  // One line per divider. A full row ring would stroke the shared divider
  // twice, and the two dash patterns would fill each other's gaps.
  // Dimmed and bright growers and sites are meshed separately so shared lines
  // stay with the boundary they belong to.
  const rowFeaturesForPlots = (plots: PlotGeoResponse[], dimmed: boolean) => {
    const plotIds = new Set(plots.map((plot) => plot.id));
    const grid = plotGeosToPlotGrid(
      plots,
      rowsGeo.filter((row) => plotIds.has(row.plot_id)),
    );
    if (grid?.mesh == null) {
      return [];
    }
    return stampDimmed(
      [
        ...toRowDividerGeoJson(grid.mesh).features,
        ...toRowLabelGeoJson(grid.mesh).features,
      ],
      dimmed,
    );
  };
  const rowBoundaryFeatures = [
    ...rowFeaturesForPlots(brightSavedPlots, isPlotGridMode),
    ...rowFeaturesForPlots(dimmedSavedPlots, true),
  ];
  const rowBoundariesGeoJson =
    rowBoundaryFeatures.length === 0
      ? EMPTY_FEATURE_COLLECTION
      : {
          type: "FeatureCollection" as const,
          features: rowBoundaryFeatures,
        };
  const dimmedRowIds = new Set(
    rowsGeo.flatMap((row) =>
      savedPlotIds.has(row.plot_id) &&
      (isPlotGridMode || dimmedSavedPlotIds.has(row.plot_id))
        ? [row.id]
        : [],
    ),
  );
  const rowFillGeoJson =
    rowFillColorById.size === 0
      ? EMPTY_FEATURE_COLLECTION
      : toRowAnalysisFillGeoJson(
          rowsGeo.filter((row) => savedPlotIds.has(row.plot_id)),
          rowFillColorById,
          dimmedRowIds,
        );
  // Kept after probe: React Compiler did not stabilize this FeatureCollection
  // across re-renders.
  const savedPlotsGeoJson = useMemo(() => {
    const featuresForPlots = (plots: PlotGeoResponse[], dimmed: boolean) => {
      if (plots.length === 0) {
        return [];
      }
      const grid = plotGeosToPlotGrid(plots);
      const gridGeoJson = grid != null ? toGridGeoJson(grid) : null;
      const trialLabels = toPlotGridTrialLabelsGeoJson(plots, trialsById);
      const collection =
        gridGeoJson == null
          ? trialLabels.features.length > 0
            ? trialLabels
            : null
          : trialLabels.features.length === 0
            ? gridGeoJson
            : {
                type: "FeatureCollection" as const,
                features: [...gridGeoJson.features, ...trialLabels.features],
              };
      if (collection == null) {
        return [];
      }
      return stampDimmed(
        applyPlotAnalysisFillColors(collection, plotFillColorById).features,
        dimmed,
      );
    };
    const features = [
      ...featuresForPlots(brightSavedPlots, isPlotGridMode),
      ...featuresForPlots(dimmedSavedPlots, true),
    ];
    if (features.length === 0) {
      return EMPTY_FEATURE_COLLECTION;
    }
    return {
      type: "FeatureCollection" as const,
      features,
    };
  }, [
    brightSavedPlots,
    dimmedSavedPlots,
    isPlotGridMode,
    plotFillColorById,
    // React Compiler stabilizes this Map; oxlint cannot see that.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    trialsById,
  ]);
  const selectedTrialLabel =
    selectedTrialId == null
      ? null
      : getTrialGeoLabel(trialsById.get(selectedTrialId), selectedTrialId);
  const gridGeoJson =
    grid != null
      ? applyPlotAnalysisFillColors(
          toGridGeoJson(grid, {
            selectedCellIds,
            selectedVertexId,
            trialLabel: isPlotGridMode ? selectedTrialLabel : null,
          }),
          plotFillColorById,
        )
      : EMPTY_FEATURE_COLLECTION;

  const interactiveLayerIds = canInsertOnEdge
    ? [
        isSiteBoundaryMode
          ? SITE_BOUNDARY_DRAFT_LINE_LAYER_ID
          : GROWER_BOUNDARY_DRAFT_LINE_LAYER_ID,
      ]
    : [];

  const mapCursor = isDragging ? "grabbing" : hoverCursor;

  return (
    <Map
      ref={mapRef}
      // Shift+drag is used for free vertex/edge move and cell bbox select after commit;
      // Mapbox box-zoom would otherwise steal that gesture.
      boxZoom={!canEditMesh}
      cursor={mapCursor}
      doubleClickZoom={!canDrawBoundaryPoints && !canEditMesh && !isDragging}
      // Ctrl/Cmd-drag on a corner rotates the grid. Mapbox drag-rotate
      // would otherwise take that gesture.
      dragRotate={
        !((canManipulateGridTransform || canEditMesh) && rotateModifierHeld)
      }
      initialViewState={US_INITIAL_VIEW}
      interactiveLayerIds={interactiveLayerIds}
      mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
      mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
      maxPitch={0}
      onClick={handleMapClick}
      onContextMenu={handleContextMenu}
      onLoad={handleMapLoad}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      pitchWithRotate={false}
      projection="mercator"
      style={{ height: "100%", width: "100%" }}
      touchPitch={false}
    >
      <LocationMarkers />
      <PlotMapLayers
        bboxGeoJson={meshInteraction.bboxGeoJson}
        boundaryVisibility={boundaryVisibility}
        gridGeoJson={gridGeoJson}
        growerBoundariesGeoJson={growerBoundariesGeoJson}
        growerDraftGeoJson={growerDraftGeoJson}
        growerDraftLabelVisibility={growerDraftLabelVisibility}
        growerDraftVisibility={growerDraftVisibility}
        growerLabelVisibility={growerLabelVisibility}
        isPlotGridMode={isPlotGridMode}
        plotGridVisibility={plotGridVisibility}
        plotLabelVisibility={plotLabelVisibility}
        rowBoundariesGeoJson={rowBoundariesGeoJson}
        rowBoundaryVisibility={rowBoundaryVisibility}
        rowFillGeoJson={rowFillGeoJson}
        rowLabelVisibility={rowLabelVisibility}
        savedPlotsGeoJson={savedPlotsGeoJson}
        showRowVertices={isPlotMapLayerVisible(prefs, "row")}
        siteBoundariesGeoJson={siteBoundariesGeoJson}
        siteBoundaryVisibility={siteBoundaryVisibility}
        siteDraftGeoJson={siteDraftGeoJson}
        siteDraftLabelVisibility={siteDraftLabelVisibility}
        siteDraftVisibility={siteDraftVisibility}
        siteLabelVisibility={siteLabelVisibility}
      />
      <CogOrthoMapLayers
        orthoId={displayedOrthoId}
        reportLodDebug={displayedOrthoId != null}
        visibility="visible"
      />
    </Map>
  );
};

export default PlotMapView;
